import { useEffect, useState, useRef, useMemo, useCallback, useDeferredValue, memo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, uploadFile, ocrScan } from "@/lib/api";
import { NOT_ACCOUNTING, notAccountingFrom409, payableTotal, withUiType, issuedPayloadFromDoc, receivedPayloadFromDoc, receiptPayload, DUP_CHECK_URL, duplicatesFrom409, invoiceFromOcr, receiptFromInvoiceOcr, receiptFromDocState, docOnProject, isLightDoc, canSeeIssued, mergeUpdatedRow, ocrCurrency, fxRateOf, foreignAmount, docMoney, shareMoney, AMOUNT_MODES, defaultAmountMode, amountOf } from "@/lib/documents";
import { AmountPick, AM_HINT, AM_HEAD } from "@/components/shared/AmountPick";
import { stockQueueQuery, dupPairsQuery, refreshEcoBadges } from "@/lib/ecoQueries";
import { DuplicateBanner, scrollToDuplicateBanner } from "@/components/shared/DuplicateBanner";
import { DuplicatesPanel, DismissedDuplicates } from "@/components/shared/DuplicatesPanel";
import { KindSwitchedHint } from "@/components/shared/DocumentReviewDialog";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/image";
import { formatCZK, formatCZKCompact, formatDate, formatDateShort, plur } from "@/lib/format";
import { todayISO, toDate, diffDays } from "@/lib/days";
import { personColor } from "@/lib/taskCalendar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard, useIsMobile } from "@/components/shared/PagePrimitives";
import { ListActions, ListToolbar, SlotActions } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { storage } from "@/lib/storage";
import { DocumentReviewDialog } from "@/components/shared/DocumentReviewDialog";
import { ReceiptDetail } from "@/components/shared/ReceiptDetail";
import { DeleteDocumentDialog } from "@/components/shared/DeleteDocumentDialog";
import { BulkDeleteDialog } from "@/components/shared/BulkDeleteDialog";
import { PaymentsDialog } from "@/components/shared/PaymentsDialog";
import { FileText, ReceiptText, Trash2, Wallet, Package, FileStack, PencilLine, MoreHorizontal, Check, Minus, ListChecks, Briefcase, CalendarDays, Tag, Tags, ChevronDown, ArrowDownLeft, ArrowUpRight, Layers, SlidersHorizontal, CircleDot, Shapes, Building2, Clock, CalendarClock, Banknote } from "lucide-react";
import { GroupSortPanel } from "@/components/shared/GroupSortPanel";
import { describeView, isDefView } from "@/components/shared/GroupSort";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDocTags, TagCell, TagMenu, TagsManagerDialog, TAGS_CHANGED } from "@/components/economics/DocTags";
import { useDocPicker } from "@/components/shared/DocPicker";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { useListNav, ListHelp } from "@/components/shared/ListNav";
import { DetailDock } from "@/components/shared/DetailDock";

const CATS_FALLBACK = ["Materiál", "Subdodávka", "Doprava", "Nářadí", "Ostatní"];
const RCATS_FALLBACK = ["PHM (pohonné hmoty)", "Kancelářské vybavení", "Ubytování (služební cesty)", "Cestovné a diety", "Stravné a reprezentace"];
const normalizePay = (v) => {
  const s = (v || "").toLowerCase();
  if (s.includes("kart") || s.includes("card")) return "Kartou";
  if (s.includes("hotov") || s.includes("cash")) return "Hotovost";
  return v || "";
};
const MONTH_NAMES = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const MONTH_LABEL = (m) => { const [y, mm] = m.split("-"); return `${MONTH_NAMES[Number(mm) - 1]} ${y}`; };
const EMPTY_STOCK = { docs: [], total: 0, count: 0 };
const EMPTY_DUPS = { pairs: [], open_count: 0 };
// Postupné vykreslení (220): seznam ukazuje prvních PAGE řádků, další na tlačítko.
// KPI, počty v segmentech, filtry i hledání běží vždy nad CELÝM seznamem.
export const PAGE = 100;
// 352: jednotná lišta — záložky Vše / Přijaté / Vydané / Účtenky, Filtr (Stav platby, Zakázka, Období,
// Kategorie, Štítek, Druh), Řadit, Nahrát doklad v hlavičce Ekonomiky.
// 373: Seskupit (stav jako v náhledu, měsíc, zakázka, štítek, kategorie, dodavatel, nebo bez skupin) a Řadit podle
// (datum vložení, vystavení, splatnost, částka, dodavatel) se směrem. Řadí se vždy uvnitř skupin; výchozí je Stav + od posledního vloženého.
// 377: obojí v jednom tlačítku (varianta B jako Airtable) — skupiny mají vlastní pořadí (gd). 378: tlačítko jen „Zobrazení“, klávesa V.
export const docStav = (x) => (x.overdue ? "late" : x.settled || ["Zaplaceno", "Vyrovnáno"].includes(x.status) ? "done" : x.paid > 0 ? "part" : "plan");
const STAV_GROUPS = [["late", "Po splatnosti"], ["plan", "Čeká na úhradu"], ["done", "Zaplacené"]];
const AZ = ["A → Z", "Z → A"];
const DOC_GROUPS = {
  none: { label: "Bez skupin" },
  status: { label: "Stav", icon: CircleDot, dirs: ["Po splatnosti první", "Zaplacené první"], key: (x) => (docStav(x) === "part" ? "plan" : docStav(x)) },
  month: { label: "Měsíc vystavení", icon: CalendarDays, dirs: ["Nejnovější první", "Nejstarší první"], key: (x) => (x.date || "").slice(0, 7) },
  project: { label: "Zakázka", icon: Briefcase, dirs: AZ, key: (x) => (x.raw?.assignment_mode === "split" ? "~split" : x.hasProject ? x.project : "") },
  tag: { label: "Štítek", icon: Tag, dirs: AZ, key: (x) => x.tag || "" },
  category: { label: "Kategorie", icon: Shapes, dirs: AZ, key: (x) => (x.kind === "vydana" ? "~vydane" : x.category || "") },
  supplier: { label: "Dodavatel / odběratel", icon: Building2, dirs: AZ, key: (x) => x.supplier || "" },
};
const groupLabel = (g, key) => (
  g === "status" ? STAV_GROUPS.find(([k]) => k === key)?.[1]
    : g === "month" ? (key ? MONTH_LABEL(key) : "Bez data")
      : key === "~split" ? "Rozděleno po položkách" : key === "~vydane" ? "Vydané faktury"
        : key || { project: "Režie (bez zakázky)", tag: "Bez štítku", category: "Bez kategorie", supplier: "Bez dodavatele" }[g]);
// Pořadí skupin (gd 0 = první popisek): Stav po splatnosti → zaplacené, měsíce od nejnovějšího, ostatní A → Z; „Bez …“ vždy na konec.
const groupRank = (k) => (!k ? 2 : k.startsWith("~") ? 1 : 0);
const groupCmp = (g, gd) => (a, b) => {
  if (g === "status") { const c = STAV_GROUPS.findIndex(([k]) => k === a) - STAV_GROUPS.findIndex(([k]) => k === b); return gd ? -c : c; }
  if (groupRank(a) !== groupRank(b)) return groupRank(a) - groupRank(b);
  const c = g === "month" ? b.localeCompare(a) : a.localeCompare(b, "cs");
  return gd ? -c : c;
};
const DOC_ORDERS = {
  created: { label: "Datum vložení", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (x) => x.created || "" },
  date: { label: "Datum vystavení", icon: CalendarDays, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (x) => x.date || "" },
  due: { label: "Splatnost", icon: CalendarClock, dirs: ["Nejdřív", "Nejpozději"], def: "asc", val: (x) => x.due || "" },
  total: { label: "Částka", icon: Banknote, dirs: ["Od nejnižší", "Od nejvyšší"], def: "desc", val: (x) => x.total || 0 },
  supplier: { label: "Dodavatel / odběratel", icon: Building2, dirs: ["A → Z", "Z → A"], def: "asc", val: (x) => x.supplier || "" },
};
// pole pro panel Seskupit a řadit (směr 0 = první popisek; u řazení sestupně)
const GROUP_FIELDS = Object.entries(DOC_GROUPS).filter(([k]) => k !== "none").map(([value, x]) => ({ value, label: x.label, icon: x.icon, dirs: x.dirs }));
const ORDER_FIELDS = Object.entries(DOC_ORDERS).map(([value, x]) => ({ value, label: x.label, icon: x.icon, dirs: [x.dirs[1], x.dirs[0]] }));
const DOC_VIEW = { g: "status", k: "created", dir: "desc", gd: 0 };
// 389: pátý údaj = Částky (net | gross | both); prázdný = výchozí podle firmy
const readDocSort = () => {
  const [g, k, d, gd, a] = String(storage.get("docs_view") || "").split("|");
  return DOC_GROUPS[g] && DOC_ORDERS[k] ? { g, k, dir: d === "asc" ? "asc" : "desc", gd: gd === "1" ? 1 : 0, a: AMOUNT_MODES[a] ? a : null } : DOC_VIEW;
};

// 387: splatnost jako den v místním čase — dřív `new Date("RRRR-MM-DD")` = půlnoc UTC a faktura splatná dnes
// byla od 1:00 / 2:00 „po splatnosti“. Přijatý dobropis po splatnosti není náš dluh.
const pastDue = (due) => !!due && String(due).slice(0, 10) < todayISO();
const isOverdue = (d) => d.payment_status !== "Zaplaceno" && !d.is_credit_note && pastDue(d.due_date);
const overdueDays = (d) => (d.due_date ? Math.max(diffDays(new Date(), toDate(d.due_date)), 0) : 0);
const OCR_SPIN = (
  <div className="flex flex-col items-center justify-center gap-4 py-6 text-center">
    <div className="w-12 h-12 rounded-full border-4 border-secondary border-t-brand animate-spin motion-reduce:animate-none" />
    <div className="font-semibold">Načítám doklad…</div>
    <div className="text-sm text-muted-foreground">OCR čte položky z dokladu</div>
  </div>
);

// `projectId` (166): tabulka vložená do Ekonomiky zakázky — jen doklady té zakázky
// (i rozdělené faktury s řádkem na ní), bez firemních KPI, duplicit a filtru Zakázka;
// nově nahraný doklad má zakázku rovnou předvyplněnou.
// dialogsOnly + openRef (229): jen detail dokladu bez seznamu — Režie a Naskladnění
// otevírají doklad na místě, bez přesměrování do Dokladů (openRef.current(kind, id)).
export default function EconomicsDocuments({ projectId = null, onChanged, dialogsOnly = false, openRef, headerExtra = null }) {
  const embedded = !!projectId;
  const { company, user } = useAuth();
  const issuedOk = canSeeIssued(user);   // MANAGER by na /issued-invoices dostal 403
  const qc = useQueryClient();
  const cats = company?.cost_categories?.length ? company.cost_categories : CATS_FALLBACK;
  const rcats = company?.receipt_categories?.length ? company.receipt_categories : RCATS_FALLBACK;
  // 365: štítky dokladů — dávají je ekonomické role (jako úpravu dokladu); okno Štítky firmy
  const canTag = ["OWNER", "MANAGER", "ASSISTANT", "ACCOUNTANT"].includes(user?.role);
  const { tags: docTags, colorOf: tagColor } = useDocTags(canTag && !dialogsOnly);
  const [tagsMgr, setTagsMgr] = useState(false);

  const [docs, setDocs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [issued, setIssued] = useState([]);
  // Fronta naskladnění a duplicity (220): sdílené s odznaky v obalu Ekonomiky (jeden
  // požadavek místo dvou); ve vložené Ekonomice zakázky se nezobrazují, tak se ani nenačítají.
  const stockQ = useQuery({ ...stockQueueQuery, enabled: !embedded }).data || EMPTY_STOCK;
  const dupPairs = useQuery({ ...dupPairsQuery, enabled: !embedded && !dialogsOnly }).data || EMPTY_DUPS;
  const [busy, setBusy] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [typeF, setTypeF] = useState("all");
  const [sort, setSortState] = useState(readDocSort);
  const setSort = (v) => { setSortState(v); storage.set("docs_view", `${v.g}|${v.k}|${v.dir}|${v.gd || 0}${v.a ? `|${v.a}` : ""}`); };
  // 389: Zobrazení › Částky — plátce bez DPH (skutečný náklad, DPH se vrací), neplátce s DPH; K úhradě vždy s DPH
  const defAm = defaultAmountMode(company, user);   // 394: osobní volba z Nastavení
  const am = sort.a || defAm;
  const amBase = am === "both" ? "net" : am;
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q);   // psaní do hledání nečeká na překreslení seznamu
  const mobile = useIsMobile();
  // 370: na PC (ne v zakázce, ne z Přehledu) se detail otevře vedle tabulky jako v Úkolech — tabulka 2 : detail 3
  const docked = !mobile && !embedded && !dialogsOnly;
  const [fileOpen, setFileOpen] = useState(false);   // ukázaná faktura v panelu — tabulka se schová
  const goRef = useRef(null);                        // klik na jiný řádek → kontrola neuložených změn v detailu

  const [params, setParams] = useSearchParams();
  // Nový doklad v zakázce → zakázka předvyplněná (jde ji v dialogu změnit).
  const pre = (o) => (projectId ? { ...o, project_id: o.project_id || projectId } : o);

  const firstLoad = useRef(true);
  // 220: v zakázce jen její doklady (server filtruje stejně jako docOnProject), lehký
  // seznam bez položek; vydané jen pro role, které je smí vidět.
  const withProject = (path) => (projectId ? `${path}${path.includes("?") ? "&" : "?"}project_id=${encodeURIComponent(projectId)}` : path);
  const load = () => {
    // Po změně (ne při prvním načtení) dá vědět rodiči — Ekonomika zakázky přepočte KPI —
    // a přenačte odznaky / frontu naskladnění / duplicity (sdílené s obalem).
    const initial = firstLoad.current;
    firstLoad.current = false;
    if (!initial) { onChanged?.(); if (!embedded) refreshEcoBadges(qc); }
    if (dialogsOnly) return;   // seznamy tu nejsou potřeba (229)
    api.get(withProject("/documents?view=list")).then((r) => setDocs(r.data)).catch(() => {});
    api.get(withProject("/receipts")).then((r) => setReceipts(r.data)).catch(() => {});
    if (issuedOk) api.get(withProject("/issued-invoices")).then((r) => setIssued(r.data)).catch(() => {});
  };
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { load(); api.get("/projects/options").then((r) => setProjects(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onSaved = () => loadRef.current();
    window.addEventListener("pp-doc-saved", onSaved);
    window.addEventListener(TAGS_CHANGED, onSaved);   // 365: přejmenovaný / smazaný štítek
    return () => { window.removeEventListener("pp-doc-saved", onSaved); window.removeEventListener(TAGS_CHANGED, onSaved); };
  }, []);
  useEffect(() => {
    const u = params.get("upload");
    if (u === "faktura" || u === "uctenka") { setParams({}, { replace: true }); setTimeout(() => docPicker.open(u), 150); }
  }, []); // eslint-disable-line

  /* ---------- FAKTURA ---------- */
  const [doc, setDoc] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Duplicity (149/153): jeden stav pro fakturu i účtenku — otevřený je vždy jen jeden dialog.
  // Jistá shoda (`certain`) = uložit nejde, patička nabídne jen Zahodit / Zobrazit existující.
  const [dups, setDups] = useState([]);
  const blocked = dups.some((d) => d.certain);
  const lastDupKey = useRef("");
  const resetDups = () => { setDups([]); lastDupKey.current = ""; };
  const docKind = (d) => (d?.ui_type === "vydana" ? "issued" : "document");
  const docPayload = (d) => (d.ui_type === "vydana" ? issuedPayloadFromDoc(d) : receivedPayloadFromDoc(d));
  const checkDups = async (kind, payload) => {
    const key = kind + JSON.stringify(payload);
    if (key === lastDupKey.current) return;
    lastDupKey.current = key;
    try { const { data } = await api.post(DUP_CHECK_URL[kind], payload); setDups(data.duplicates || []); } catch { /* jen nápověda */ }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setOcrLoading(true);
    try {
      const url = await uploadFile(f);
      const data = await ocrScan("document", url);
      if (data.doc_type === "uctenka" && !NOT_ACCOUNTING[data.doc_kind]) {
        // Účtenka nahraná přes „Nahrát fakturu" (150) → rovnou kontrola účtenky, ne faktury.
        // 386: objednávka / dodací list zůstane v okně faktury s upozorněním.
        const next = pre(receiptFromInvoiceOcr(data, url));
        resetDups(); setRec(next); checkDups("receipt", receiptPayload(next));
        setOcrLoading(false); setRecOpen(true);
        return;
      }
      // Typ předvyplní OCR podle IČO/DIČ/názvu firmy (zadání 148) — supplier_* nese protistranu.
      const next = pre(invoiceFromOcr(data, url, cats, company?.currency));
      resetDups(); setDoc(next);
      checkDups(docKind(next), docPayload(next));
      setOcrLoading(false); setReviewOpen(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Doklad se nepodařilo načíst."); }
    finally { setBusy(false); setOcrLoading(false); }
  };
  // Ruční „Typ → Účtenka" u čerstvého skenu faktury (150): upravená pole → účtenka.
  const becomeReceipt = () => {
    const next = pre(receiptFromDocState(doc));
    setReviewOpen(false); setDoc(null);
    resetDups(); setRec(next); checkDups("receipt", receiptPayload(next)); setRecOpen(true);
  };
  // Cesta zpět: „Otevřít jako fakturu" z účtenky rozpoznané přes Nahrát fakturu.
  const openAsInvoice = () => {
    const next = pre(invoiceFromOcr(rec.ocr_raw || {}, rec.file_url, cats, company?.currency));
    setRecOpen(false); setRec(null);
    resetDups(); setDoc(next); checkDups(docKind(next), docPayload(next)); setReviewOpen(true);
  };
  // `ui_type` je stav dialogu (přijatá × vydaná). NEplést s backendovým `doc_type`,
  // který rozlišuje fakturu od účtenky a zůstává beze změny.
  // Řádek lehkého seznamu nemá položky (220) — Kontrola dokladu dostane celý doklad,
  // jinak by „Uložit" položky smazalo. Snímek „neuložených změn" se bere při otevření,
  // proto se dialog otevře až s celým dokladem.
  const openSeq = useRef(0);
  const openDoc = async (d, closeRec = false) => {
    const seq = ++openSeq.current;
    let full = d;
    if (isLightDoc(d)) {
      try { full = { ...d, ...(await api.get(`/documents/${d.id}`)).data }; }
      catch (e) { if (seq === openSeq.current) toast.error(formatApiError(e.response?.data?.detail) || "Doklad se nepodařilo načíst."); return; }
      if (seq !== openSeq.current) return;   // mezitím otevřel jiný doklad
    }
    if (closeRec) { setRecOpen(false); setRec(null); }   // 370: panel se mezi účtenkou a fakturou nezavře
    resetDups(); setDoc({ ...full, ui_type: "prijata", line_items: full.line_items || [] }); setReviewOpen(true);
  };
  const openIssued = (inv) => {
    // Vydaná faktura má v databázi jiné názvy polí — do dialogu jde pod stejnými.
    resetDups();
    setDoc({ ...inv, ui_type: "vydana", supplier_name: inv.client_name,
             supplier_ico: inv.client_ico, supplier_dic: inv.client_dic, line_items: [] });
    setReviewOpen(true);
  };

  // Přepnutí typu. U nového (neuloženého) dokladu jen přepne formulář; u uloženého
  // je to přesun mezi kolekcemi — dělá ho server, ať se nemůže zaseknout v půlce.
  const changeType = async (v) => {
    const wantIssued = v === "vydana";
    if (wantIssued === (doc.ui_type === "vydana")) return;
    if (!doc.id) { setDoc((d) => withUiType(d, v)); return; }   // nový sken: prohodí protistranu
    const msg = wantIssued
      ? "Převést na vydanou fakturu? Rozdělení na zakázky a kategorie se u výnosu nedrží; úhrady i sken zůstávají. Najdete ji pak pod chipem Vydané a v Platbách → Inkasovat."
      : "Převést zpět na přijatou fakturu? Doklad se zařadí celý, kategorii si pak nastavíte.";
    if (!await ask(msg)) return;
    setBusy(true);
    try {
      const url = wantIssued ? `/documents/${doc.id}/convert-to-issued` : `/issued-invoices/${doc.id}/convert-to-received`;
      const { data } = await api.post(url);
      toast.success(wantIssued ? "Převedeno na vydanou fakturu." : "Převedeno na přijatou fakturu.");
      if (wantIssued) openIssued(data); else openDoc(data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveDoc = async () => {
    setBusy(true);
    try {
      if (doc.ui_type === "vydana") {
        const payload = issuedPayloadFromDoc(doc);
        if (!payload.invoice_number.trim() || !payload.client_name.trim()) {
          toast.error("Číslo faktury a odběratel jsou povinné.");
          return;
        }
        const { data } = doc.id ? await api.patch(`/issued-invoices/${doc.id}`, payload) : await api.post("/issued-invoices", payload);
        // 288: doklad k platbě se připojí k záloze — nevzniká druhá záloha.
        toast.success(data?.tax_doc_attached ? `Doklad k platbě připojen k záloze č. ${data.invoice_number || "—"}.` : "Vydaná faktura uložena.");
      } else {
        const payload = receivedPayloadFromDoc(doc);   // bez UI polí (ui_type, direction, ocr_parties…)
        const { data } = doc.id ? await api.patch(`/documents/${doc.id}`, payload) : await api.post("/documents", payload);
        // 291: doklad k platbě od dodavatele se připojí k záloze — nevzniká druhý záznam.
        toast.success(data?.tax_doc_attached ? `Doklad k platbě připojen k záloze č. ${data.invoice_number || "—"}.`
          : !doc.id && data?.internal_no ? `Doklad uložen jako FP ${data.internal_no}.` : "Doklad uložen.");
      }
      setReviewOpen(false); setDoc(null); load();
    } catch (e) {
      const d = duplicatesFrom409(e);
      if (d) { setDups(d); scrollToDuplicateBanner(); return; }   // banner místo toastu (149); blocked skryje Uložit (153)
      const na = notAccountingFrom409(e);                           // 386: server poznal objednávku / dodací list
      if (na) { setDoc((x) => ({ ...x, doc_kind: na.kind, doc_title: na.title, confirm_kind: false })); toast.error(na.message); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  // Znovu zkontrolovat duplicity 600 ms po změně strany / čísla / částky / data — jen u nového dokladu.
  useEffect(() => {
    if (!reviewOpen || !doc || doc.id) return;
    const t = setTimeout(() => checkDups(docKind(doc), docPayload(doc)), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewOpen, doc?.ui_type, doc?.supplier_name, doc?.supplier_ico, doc?.invoice_number, doc?.variable_symbol, doc?.total_with_vat, doc?.issue_date]);
  /* ---------- MAZÁNÍ S DOPADEM (160) ---------- */
  // Jeden dialog pro koš v seznamu, patičku Kontroly dokladu i detail účtenky.
  // kind = řeč seznamu (faktura/uctenka/vydana) i backendu (document/receipt/issued).
  const [delTarget, setDelTarget] = useState(null);
  const askDelete = (kind, id) => setDelTarget({ kind, id });
  // 272/283: hromadné smazání OZNAČENÝCH dokladů — jen majitel a asistentka, ne v zakázce.
  // PC: zaškrtávátka v tabulce; telefon: režim „Vybrat“. Klíč řádku = druh-id.
  const [bulkOpen, setBulkOpen] = useState(false);
  const canBulk = !embedded && ["OWNER", "ASSISTANT"].includes(user?.role);
  const canSelect = canBulk || canTag;   // 365: vybrat více dokladů jde i kvůli štítku (i v zakázce); mazat dál jen canBulk
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [selMode, setSelMode] = useState(false);       // telefon: karty v režimu výběru
  const toggleSel = useCallback((x) => setSelected((cur) => {
    const next = new Set(cur); const k = `${x.kind}-${x.id}`;
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  }), []);
  const clearSel = () => { setSelected(new Set()); setSelMode(false); };
  const onDeleted = () => {
    setDelTarget(null);
    setReviewOpen(false); setDoc(null);
    setRecOpen(false); setRec(null);
    load();
  };

  /* ---------- PLATBY ---------- */
  // Dialog úhrad je sdílený s kartou Platby (components/shared/PaymentsDialog).
  const [payDoc, setPayDoc] = useState(null);

  /* ---------- ÚČTENKA ---------- */
  const [rec, setRec] = useState(null);
  const [recOpen, setRecOpen] = useState(false);
  const isNewRec = rec && !rec.id;
  const detailOpen = (reviewOpen && !!doc) || (recOpen && !!rec);
  // 370: po zavření panelu kurzor zpět na vybraný řádek — mezerník ho zase otevře
  const kbSelRef = useRef(-1);
  const wasOpen = useRef(false);
  // 374: po otevření panelu dojede tabulka s panelem k hornímu okraji (KPI odjedou nahoru), ať je detail
  // vysoký přes celé okno. Jen když je tabulka níž — kdo je v seznamu dole, toho stránka neposune.
  const splitRef = useRef(null);
  useEffect(() => {
    if (wasOpen.current && !detailOpen && docked) document.querySelector(`[data-nav="docs:${kbSelRef.current}"]`)?.focus?.({ preventScroll: true });
    if (!wasOpen.current && detailOpen && docked) {
      const el = splitRef.current;
      if (el && el.getBoundingClientRect().top > 32) {
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        el.scrollIntoView?.({ block: "start", behavior: reduce ? "auto" : "smooth" });
      }
    }
    wasOpen.current = detailOpen;
  }, [detailOpen, docked]);
  const onRecFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setOcrLoading(true);
    try {
      const url = await uploadFile(await compressImage(f));
      const data = await ocrScan("receipt", url);
      const items = (data.items || []).map((it) => ({ description: it.description || "", quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 }));
      const next = { file_url: url, supplier_name: data.supplier_name || "", address: data.address || "", receipt_number: data.receipt_number || "", date: (data.date || "").slice(0, 10), total: Number(data.total) || 0, vat_amount: Number(data.vat_amount) || 0, payment_method: normalizePay(data.payment_method), category: "", project_id: null, items, currency: ocrCurrency(data.currency) };
      resetDups(); setRec(pre(next));
      checkDups("receipt", receiptPayload(pre(next)));
      setOcrLoading(false); setRecOpen(true);
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Údaje z účtenky se nepodařilo načíst. Zkuste vyfotit znovu." : (formatApiError(err.response?.data?.detail) || "Nahrání selhalo."));
    } finally { setBusy(false); setOcrLoading(false); }
  };
  // 184: „Nahrát fakturu / účtenku" rovnou otevře výběr fotky / souboru (bez mezikroku).
  const docPicker = useDocPicker({ onPick: (f, type) => (type === "uctenka" ? onRecFile : onFile)({ target: { files: [f] } }) });
  const openRec = (r) => { resetDups(); setRec({ ...r, payment_method: normalizePay(r.payment_method) }); setRecOpen(true); };
  // Ruční zadání bez skenování — prázdný doklad/účtenka, file_url: null.
  const openManualDoc = () => {
    resetDups();
    setDoc({ doc_type: "faktura", ui_type: "prijata", file_url: null, payment_status: "Nezaplaceno",
             assignment_mode: "whole", project_id: projectId, category: cats[0], line_items: [],
             currency: company?.currency || "CZK", supplier_name: "", invoice_number: "",
             total_without_vat: 0, vat_amount: 0, total_with_vat: 0 });
    setReviewOpen(true);
  };
  const openManualRec = () => {
    resetDups();
    setRec({ file_url: null, supplier_name: "", address: "", receipt_number: "", date: "", total: 0, vat_amount: 0,
             payment_method: "", category: "", project_id: projectId, items: [] });
    setRecOpen(true);
  };
  const saveRec = async () => {
    if (!rec.category) return toast.error("Vyberte kategorii výdaje.");
    setBusy(true);
    try {
      await api.post("/receipts", receiptPayload(rec));
      toast.success("Účtenka uložena."); setRecOpen(false); setRec(null); load();
    } catch (e) {
      const d = duplicatesFrom409(e);
      if (d) { setDups(d); scrollToDuplicateBanner(); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (!recOpen || !rec || rec.id) return;
    const t = setTimeout(() => checkDups("receipt", receiptPayload(rec)), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recOpen, rec?.supplier_name, rec?.receipt_number, rec?.total, rec?.date]);

  // „Zobrazit" z banneru: otevře existující doklad. Stejná cesta obsluhuje
  // `?open=kind:id` z globálního „+" (DocUploadOverlay naviguje sem).
  // `kind` z backendu = řeč řádků seznamu: faktura / uctenka / vydana (150); staré názvy pro jistotu taky.
  const openById = (kind, id) => {
    if (kind === "faktura" || kind === "document") { const d = docs.find((x) => x.id === id); if (d) { openDoc(d, true); return true; } }
    if (kind === "uctenka" || kind === "receipt") { const r = receipts.find((x) => x.id === id); if (r) { setReviewOpen(false); setDoc(null); openRec(r); return true; } }
    if (kind === "vydana" || kind === "issued") { const i = issued.find((x) => x.id === id); if (i) { setRecOpen(false); setRec(null); openIssued(i); return true; } }
    return false;
  };
  const extSeq = useRef(0);   // 343: při rychlém listování vyhraje poslední vybraný doklad
  const openExternal = async (kind, id) => {
    const seq = ++extSeq.current;
    try {
      if (kind === "faktura") {
        const { data } = await api.get(`/documents/${id}`);
        if (seq !== extSeq.current) return;
        setRecOpen(false); setRec(null); openDoc(data); return;
      }
      if (kind === "uctenka") {
        const { data } = await api.get("/receipts"); setReceipts(data);
        const r = data.find((x) => x.id === id);
        if (r) { setReviewOpen(false); setDoc(null); openRec(r); return; }
      }
      toast.error("Doklad už v seznamu není.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Doklad se nepodařilo načíst."); }
  };
  // 343: `list` = { items: [{ kind, id }], onIndex } — volající (Přehled) chce v detailu listovat svým seznamem.
  const [extList, setExtList] = useState(null);
  if (openRef) openRef.current = (kind, id, list = null) => { setExtList(list); return openExternal(kind, id); };
  const showDup = (d) => { if (!openById(d.kind, d.id)) toast.error("Doklad už v seznamu není."); };
  useEffect(() => {
    const o = params.get("open");
    if (!o) return;
    const [kind, id] = o.split(":");
    if (!docs.length && !receipts.length && !issued.length) return;   // seznamy se ještě načítají
    openById(kind, id);
    const next = new URLSearchParams(params); next.delete("open");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, docs, receipts, issued]);
  const saveRecEdit = async (withHead) => {
    if (!rec.category) return toast.error("Vyberte kategorii výdaje.");
    setBusy(true);
    try {
      // 229: s tužkou (úprava hlavičky) i obchod, adresa, datum, číslo, celkem a DPH
      const head = withHead ? { supplier_name: rec.supplier_name || "", address: rec.address || "", receipt_number: rec.receipt_number || "",
        date: rec.date || null, total: Number(rec.total) || 0, vat_amount: Number(rec.vat_amount) || 0,
        ...(rec.currency || fxRateOf(rec) ? { currency: rec.currency || null, fx_rate: fxRateOf(rec) } : {}) } : {};    // 321
      await api.patch(`/receipts/${rec.id}`, { payment_method: rec.payment_method || null, category: rec.category, project_id: rec.project_id || null, tag: rec.tag || null, ...head });   // 372: i štítek
      toast.success("Změny uloženy."); setRecOpen(false); setRec(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeRec = (id) => askDelete("receipt", id);

  /* ---------- SEZNAM ---------- */

  // Období: nabídka posledních 12 měsíců, které v datech existují.
  const periods = useMemo(() => {
    const s = new Set();
    docs.forEach((d) => d.issue_date && (!projectId || docOnProject(d, "faktura", projectId)) && s.add(d.issue_date.slice(0, 7)));
    receipts.forEach((r) => (r.date || r.created_at) && (!projectId || r.project_id === projectId) && s.add((r.date || r.created_at).slice(0, 7)));
    issued.forEach((d) => d.issue_date && (!projectId || d.project_id === projectId) && s.add(d.issue_date.slice(0, 7)));
    return [...s].sort().reverse().slice(0, 12);
  }, [docs, receipts, issued, projectId]);

  // Jeden tvar pro fakturu i účtenku, ať se seznam nemusí větvit na každém řádku.
  const all = useMemo(() => {
    const a = docs.map((d) => ({
      kind: "faktura", id: d.id, raw: d, date: d.issue_date, due: d.due_date, created: d.created_at || null,
      supplier: d.supplier_name, project: d.assignment_mode === "split" ? "rozděleno" : (d.project_name || "Režie"),
      hasProject: !!d.project_id, project_id: d.project_id || null,
      // 387: v zakázce u rozdělené faktury jen podíl zakázky (server `project_share`), jinak celá částka
      total: projectId && d.project_share != null ? d.project_share : d.total_with_vat || d.total_without_vat || 0,
      amt: projectId && d.project_share != null ? shareMoney(d, docMoney(d, "faktura")) : docMoney(d, "faktura"),   // 389
      due_total: payableTotal(d), payable: payableTotal(d), paid: d.paid_total || 0,
      settled: d.invoice_type === "zaloha" && !!d.settled_by_id,     // 387: vyúčtovaná záloha — částka je už v konečné faktuře
      status: d.payment_status, overdue: isOverdue(d), overdueDays: isOverdue(d) ? overdueDays(d) : 0,
      credit: !!d.is_credit_note, no: d.internal_no || null, series: "FP", tag: d.tag || null, category: d.category || null,
      sub: `č. ${d.invoice_number || "—"}${d.category ? ` · ${d.category}` : ""}${d.is_credit_note && d.credited_invoice_number ? ` · k faktuře č. ${d.credited_invoice_number}` : ""}`
        // 291: záloha od dodavatele není náklad; konečná jen doplatek
        + (d.invoice_type === "zaloha" ? (d.settled_by_id ? ` · vyúčtováno fakturou č. ${d.settled_by_number || "—"}` : " · záloha (náklad až s konečnou)")
          : d.invoice_type === "konecna" ? " · konečná" : "")
        + (d.tax_doc?.number ? ` · doklad k platbě č. ${d.tax_doc.number}` : "")
        + (d.advances_deducted_gross > 0 ? ` · zálohy −${formatCZK(d.advances_deducted_gross)}` : "")
        // 292: import přes Claude kategorii nepoznal — uloží se „Materiál“ a čeká na kontrolu
        + ((d.import_check || []).includes("category") ? " · doplnit kategorii" : "")
        + (projectId && d.project_share != null ? ` · podíl zakázky z ${formatCZK(d.total_with_vat || d.total_without_vat || 0)}` : ""),
      fx: foreignAmount(d),                         // 324: faktura v cizí měně
    }));
    const b = receipts.map((r) => ({
      kind: "uctenka", id: r.id, raw: r, date: r.date || r.created_at, due: null, created: r.created_at || null,
      supplier: r.supplier_name, project: r.project_name || "Režie",
      hasProject: !!r.project_id, project_id: r.project_id || null,
      total: r.total, paid: r.total, status: r.payment_status || "Zaplaceno", overdue: false, overdueDays: 0, credit: false, amt: docMoney(r, "uctenka"),
      no: r.internal_no || null, series: "UC", tag: r.tag || null, category: r.category || null,
      sub: `${r.payment_method || "—"} · ${r.category || "—"}${r.receipt_number ? ` · č. ${r.receipt_number}` : ""}`,
      fx: foreignAmount(r),
    }));
    return [...a, ...b].filter((x) => !projectId || docOnProject(x.raw, x.kind, projectId))
      .sort((x, y) => String(y.date || "").localeCompare(String(x.date || "")));
  }, [docs, receipts, projectId]);

  // Vydané faktury (všechna období) — VÝNOS, do peněžních KPI nepatří.
  const issuedAll = useMemo(() => issued
    .filter((d) => !projectId || d.project_id === projectId)
    .map((d) => ({
      kind: "vydana", id: d.id, raw: d, date: d.issue_date, due: d.due_date, created: d.created_at || null, no: d.internal_no || null, series: "FV", tag: d.tag || null, category: null,
      supplier: d.client_name, project: d.project_name || "Bez zakázky",
      hasProject: !!d.project_id, project_id: d.project_id || null,
      total: d.total_with_vat || d.total_without_vat || 0, paid: d.paid_total || 0, amt: docMoney(d, "vydana"),
      payable: payableTotal(d),    // 171: u konečné faktury jen doplatek po zálohách
      status: d.payment_status, overdue: isOverdue(d), overdueDays: overdueDays(d),
      credit: !!d.is_credit_note,
      settled: d.invoice_type === "zaloha" && !!d.settled_by_id,
      advPending: d.advances_pending || 0,
      sub: `č. ${d.invoice_number || "—"}${d.is_credit_note && d.credited_invoice_number ? ` · k faktuře č. ${d.credited_invoice_number}` : ""}`
        + (d.invoice_type === "zaloha" ? (d.settled_by_id ? ` · vyúčtováno fakturou č. ${d.settled_by_number || "—"}` : " · záloha") : "")
        + (d.tax_doc?.number ? ` · doklad k platbě č. ${d.tax_doc.number}` : "")
        + (d.advances_deducted_gross > 0 ? ` · zálohy −${formatCZK(d.advances_deducted_gross)}` : ""),
      fx: foreignAmount(d),
    })), [issued, projectId]);

  const docGroups = useMemo(() => [
    { key: "platba", label: "Stav platby", icon: Wallet,
      options: [{ value: "Zaplaceno", label: "Zaplaceno" }, { value: "Částečně zaplaceno", label: "Částečně" }, { value: "Nezaplaceno", label: "Nezaplaceno" }, { value: "Po splatnosti", label: "Po splatnosti" }],
      match: (x, v) => (v === "Po splatnosti" ? !!x.overdue : x.status === v) },
    ...(!embedded ? [{ key: "zakazka", label: "Zakázka", icon: Briefcase,
      options: [{ value: "rezie", label: "Režie (bez zakázky)" }, ...projects.map((p) => ({ value: p.id, label: p.name }))],
      match: (x, v) => (v === "rezie" ? !x.hasProject : docOnProject(x.raw, x.kind, v)) }] : []),
    { key: "obdobi", label: "Období", icon: CalendarDays, options: periods.map((m) => ({ value: m, label: MONTH_LABEL(m) })),
      match: (x, v) => (x.date || "").startsWith(v) },
    { key: "kategorie", label: "Kategorie", icon: Tag, options: [...new Set([...cats, ...rcats])].map((c) => ({ value: c, label: c })),
      match: (x, v) => x.raw?.category === v },
    ...(canTag ? [{ key: "stitek", label: "Štítek", icon: Tags,
      options: [...docTags.map((t) => ({ value: t.name, label: t.name })), { value: "__none", label: "Bez štítku" }],
      match: (x, v) => (v === "__none" ? !x.tag : x.tag === v) }] : []),
    { key: "druh", label: "Druh dokladu", icon: FileStack, options: [{ value: "credit", label: "Dobropis" }], match: (x) => !!x.credit },
  ], [embedded, projects, periods, cats, rcats, canTag, docTags]);
  const allRows = useMemo(() => [...all, ...issuedAll], [all, issuedAll]);
  const F = useListFilters(allRows, { groups: docGroups, entity: { one: "doklad", few: "doklady", many: "dokladů" } });
  const fKey = JSON.stringify(F.state);
  // KPI se počítá za VYBRANÉ OBDOBÍ (Filtr › Období), ne za filtrovaný seznam — jinak by čísla
  // skákala při každém kliknutí na pilulku a ztratila by výpovědní hodnotu.
  const perF = F.state.obdobi;
  const inPer = useCallback((x) => {
    if (!perF) return true;
    const has = perF.vals.some((v) => (x.date || "").startsWith(v));
    return perF.neg ? !has : has;
  }, [JSON.stringify(perF)]); // eslint-disable-line react-hooks/exhaustive-deps
  const inPeriod = useMemo(() => all.filter(inPer), [all, inPer]);
  const kpi = useMemo(() => {
    const inv = inPeriod.filter((x) => x.kind === "faktura");
    const rec = inPeriod.filter((x) => x.kind === "uctenka");
    const due = (x) => x.due_total ?? x.total ?? 0;      // 291: konečná od dodavatele — jen K úhradě
    // 387: přijatý dobropis není náš dluh; vyúčtovaná záloha je už v konečné faktuře a dobropis se odečítá
    const unpaid = inPeriod.filter((x) => !x.credit && Math.max(due(x) - (x.paid || 0), 0) > 0.5);
    const sum = (list, base = amBase) => list.reduce((s, x) => s + (x.settled ? 0 : (x.credit ? -1 : 1) * amountOf(x.amt, base)), 0);
    const over = unpaid.filter((x) => x.overdue);
    const rest = (list) => list.reduce((s, x) => s + Math.max(due(x) - (x.paid || 0), 0), 0);
    return {
      count: inPeriod.length, invoices: inv.length, receipts: rec.length,
      total: sum(inPeriod), totalGross: sum(inPeriod, "gross"),
      overhead: sum(inPeriod.filter((x) => !x.hasProject)),
      unpaid: rest(unpaid), unpaidCount: unpaid.length,
      overdue: rest(over), overdueCount: over.length,
      oldest: over.reduce((m, x) => Math.max(m, x.overdueDays), 0),
    };
  }, [inPeriod, amBase]);

  // Počty v záložkách = po filtrech (bez hledání), „Vše" ukazuje i vydané (132/A).
  const counts = useMemo(() => ({
    all: F.visible.length,
    faktury: F.visible.filter((x) => x.kind === "faktura").length,
    uctenky: F.visible.filter((x) => x.kind === "uctenka").length,
    vydane: F.visible.filter((x) => x.kind === "vydana").length,
  }), [F.visible]);
  const vydaneInPeriod = useMemo(() => issuedAll.filter(inPer).length, [issuedAll, inPer]);

  const merged = useMemo(() => {
    let list = F.visible;
    if (typeF === "faktury") list = list.filter((x) => x.kind === "faktura");
    else if (typeF === "uctenky") list = list.filter((x) => x.kind === "uctenka");
    else if (typeF === "vydane") list = list.filter((x) => x.kind === "vydana");
    if (dq.trim()) {
      // Hledá napříč dodavatelem, číslem dokladu i názvem zakázky (dřív jen dodavatel).
      // 224/229: „#444", „444", „FP 444", „uc46" najde i podle interního čísla dokladu.
      const s = dq.trim().toLowerCase();
      const m = s.match(/^(#|fp|uc|fv)?\s*(\d+)$/);
      const no = m ? Number(m[2]) : null;
      const ser = m && m[1] && m[1] !== "#" ? m[1].toUpperCase() : null;
      list = list.filter((x) => (no !== null && x.no === no && (!ser || x.series === ser)) || `${x.supplier || ""} ${x.sub} ${x.project}`.toLowerCase().includes(s));
    }
    // Řadit (352): prázdná hodnota vždy na konec; shoda → novější první
    const { k, dir } = sort, sign = dir === "desc" ? -1 : 1, val = DOC_ORDERS[k].val;
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (!va !== !vb) return va ? -1 : 1;
      const c = typeof va === "number" ? (va - vb) : String(va).localeCompare(String(vb), "cs");
      return c * sign || String(b.created || b.date || "").localeCompare(String(a.created || a.date || ""));
    });
  }, [F.visible, typeF, dq, sort]);
  // 365: skupiny (rozklikávací lišty); sbalená skupina nemá řádky v klávesnici ani v „Zobrazit další“.
  // 373: skupiny podle Seskupit, řádky v nich podle Řadit podle.
  const [closedGroups, setClosedGroups] = useState(() => new Set());
  useEffect(() => { setClosedGroups(new Set()); }, [sort.g]);
  const groups = useMemo(() => {
    const fn = DOC_GROUPS[sort.g]?.key;
    if (!fn) return null;
    const map = new Map();
    merged.forEach((x) => { const key = fn(x); if (!map.has(key)) map.set(key, []); map.get(key).push(x); });
    return [...map.entries()].sort(([a], [b]) => groupCmp(sort.g, sort.gd || 0)(a, b)).map(([key, rows]) => {
      // 401: ve Vše jsou vydané faktury tržba — do součtu nákladů a „nezaplaceno“ nepatří, ukážou se zvlášť
      const cost = rows.every(isRev) ? rows : rows.filter((x) => !isRev(x));
      return {
        key, label: groupLabel(sort.g, key), rows,
        sum: cost.reduce((t, x) => t + signedAmt(x, amountOf(x.amt, amBase)), 0),
        sumGross: cost.reduce((t, x) => t + signedAmt(x, x.amt.gross), 0),
        rev: cost === rows ? 0 : rows.filter(isRev).reduce((t, x) => t + signedAmt(x, amountOf(x.amt, amBase)), 0),
        unpaid: cost.reduce((t, x) => t + (x.credit ? 0 : Math.max((x.payable ?? x.due_total ?? x.total ?? 0) - (x.paid || 0), 0)), 0),
      };
    });
  }, [merged, sort.g, sort.gd, amBase]);
  const ordered = useMemo(() => (groups ? groups.filter((g) => !closedGroups.has(g.key)).flatMap((g) => g.rows) : merged), [groups, closedGroups, merged]);
  const toggleGroup = useCallback((key) => setClosedGroups((cur) => { const n = new Set(cur); if (n.has(key)) n.delete(key); else n.add(key); return n; }), []);
  // Postupné vykreslení (220): po změně filtru / hledání zase od začátku.
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [typeF, fKey, dq, sort]);
  const shown = useMemo(() => ordered.slice(0, limit), [ordered, limit]);
  // 283: jiný filtr = jiný seznam — výběr se zahodí, ať se nesmaže nic, co už není vidět.
  useEffect(() => { setSelected(new Set()); }, [typeF, fKey, dq]);
  const selRows = useMemo(() => merged.filter((x) => selected.has(`${x.kind}-${x.id}`)), [merged, selected]);
  const allSel = merged.length > 0 && selRows.length === merged.length;
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(merged.map((x) => `${x.kind}-${x.id}`)));
  const selectable = canSelect && (!mobile || selMode);

  // 365: štítek jednomu nebo více dokladům — řádky se srovnají hned, Ekonomika zakázky přepočte koláč štítků
  const assignTag = async (rows, tag) => {
    try {
      await api.post("/doc-tags/assign", { tag, items: rows.map((x) => ({ kind: x.kind, id: x.id })) });
      const keys = new Set(rows.map((x) => `${x.kind}-${x.id}`));
      const upd = (kind) => (list) => list.map((d) => (keys.has(`${kind}-${d.id}`) ? { ...d, tag } : d));
      setDocs(upd("faktura")); setReceipts(upd("uctenka")); setIssued(upd("vydana"));
      qc.invalidateQueries({ queryKey: ["doc-tags"] });
      onChanged?.();
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const bulkTag = async (tag) => {
    setBulkTagOpen(false);
    const n = selRows.length;
    if (await assignTag(selRows, tag)) { toast.success(`${tag ? `Štítek ${tag}` : "Štítek odebrán"} · ${plur(n, "doklad", "doklady", "dokladů")}`); clearSel(); }
  };

  // Stabilní obsluha řádků (220): seznam je React.memo, takže psaní v dialogu
  // (stav doc / rec v téže komponentě) ho nepřekresluje.
  const rowActions = useRef({});
  rowActions.current = {
    open: async (x) => {
      // 370: s otevřeným panelem klik na jiný řádek jako ↑ ↓ — neuložené změny se zeptají, rozpracovaný nový doklad taky
      if (docked && detailOpen) {
        const i = shown.findIndex((r) => r.kind === x.kind && r.id === x.id);
        if (goRef.current && i >= 0) { goRef.current(i); return; }
        if (((reviewOpen && doc && !doc.id) || (recOpen && rec && !rec.id)) && !await ask("Zahodit rozpracovaný nový doklad a otevřít jiný?")) return;
      }
      if (x.kind === "vydana") { setRecOpen(false); setRec(null); openIssued(x.raw); }
      else if (x.kind === "faktura") openDoc(x.raw, true);
      else { setReviewOpen(false); setDoc(null); openRec(x.raw); }
    },
    pay: (x) => setPayDoc({ raw: x.raw, kind: x.kind }),
    del: (x) => askDelete(x.kind, x.id),
    tag: (x, t) => assignTag([x], t),
    manage: () => setTagsMgr(true),
  };
  const onRowOpen = useCallback((x) => rowActions.current.open(x), []);
  const onRowPay = useCallback((x) => rowActions.current.pay(x), []);
  const onRowDelete = useCallback((x) => rowActions.current.del(x), []);
  const onMore = useCallback(() => setLimit((l) => l + PAGE), []);
  const onRowTag = useCallback((x, t) => rowActions.current.tag(x, t), []);
  const onManageTags = useCallback(() => rowActions.current.manage(), []);
  const tagColors = useMemo(() => Object.fromEntries(docTags.map((t) => [t.name, t.color])), [docTags]);
  // 343: klávesnice (Linear / Narel) — ↑↓ nebo myš výběr řádku, mezerník otevře detail dokladu,
  // v detailu ↑↓ další doklad seznamu („2 / 5"). Pořadí se bere z otevřeného dokladu, ne z kliknutí,
  // takže sedí i po otevření myší. Přehled předá vlastní seznam přes openRef (extList).
  // 370: s otevřeným panelem listuje detail (useDetailNav — hlídá neuložené změny), seznam jen drží výběr
  const kb = useListNav("docs", shown.length, (i) => onRowOpen(shown[i]), { enabled: !mobile && !dialogsOnly && !(docked && detailOpen) });
  kbSelRef.current = kb.sel;
  // 381: najetí myší vybere řádek (jako v Úkolech) — mezerník pak otevře doklad pod myší; s otevřeným panelem
  // myš výběr nemění (drží se otevřený doklad)
  const navLocked = docked && detailOpen;
  const kbRowProps = kb.rowProps;
  const rowNav = useCallback((i) => { const p = kbRowProps(i); if (navLocked) delete p.onMouseMove; return p; }, [kbRowProps, navLocked]);
  const openId = reviewOpen ? doc?.id : recOpen ? rec?.id : null;
  const listIdx = !mobile && openId ? shown.findIndex((r) => r.id === openId) : -1;
  const extIdx = !mobile && openId && extList ? extList.items.findIndex((r) => r.id === openId) : -1;
  useEffect(() => { if (listIdx >= 0) kb.setSel(listIdx); }, [listIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  const detailNav = listIdx >= 0 ? { index: listIdx, count: shown.length, onGo: (i) => openById(shown[i].kind, shown[i].id) }
    : extIdx >= 0 ? { index: extIdx, count: extList.items.length, onGo: (i) => { extList.onIndex?.(i); openExternal(extList.items[i].kind, extList.items[i].id); } }
      : null;

  // Nahrát doklad (N) a ⋯ — v hlavičce Ekonomiky (SlotActions), v zakázce vedle nadpisu.
  const headerActions = {
    mobile, search: q, onSearch: setQ, searchPh: "Dodavatel, číslo, FP 444, zakázka…",
    primary: { label: "Nahrát doklad", full: "Nahrát doklad", testid: "add-doc-btn", disabled: busy, menu: [
      { icon: FileText, label: "Nahrát fakturu", onClick: () => docPicker.open("faktura"), testid: "add-doc-invoice" },
      { icon: ReceiptText, label: "Nahrát účtenku", onClick: () => docPicker.open("uctenka"), testid: "add-doc-receipt" },
      { icon: PencilLine, label: "Zadat fakturu ručně", onClick: openManualDoc, testid: "add-doc-manual-invoice" },
      { icon: ReceiptText, label: "Zadat účtenku ručně", onClick: openManualRec, testid: "add-doc-manual-receipt" },
    ],
    // 334: na PC jde doklad přetáhnout kamkoli do okna (Layout → useDocDrop)
    menuFooter: <div className="hidden md:block mt-1 border-t border-border px-2 pt-2 pb-1 text-xs text-muted-foreground" data-testid="add-doc-drop-hint">PDF z pošty nebo ze složky stačí přetáhnout myší do okna.</div> },
    more: mobile && canSelect && merged.length > 0
      ? [{ icon: ListChecks, label: selMode ? "Zrušit výběr" : "Vybrat doklady", onClick: () => (selMode ? clearSel() : setSelMode(true)), testid: "docs-select-mode" }] : [],
  };
  // FAKTURA — kontrola / detail dokladu; ÚČTENKA — detail podle varianty 1 (229)
  const reviewEl = (
    <DocumentReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} doc={doc} setDoc={setDoc}
        projects={projects} cats={cats} busy={busy} onSave={saveDoc} onTypeChange={changeType} onReload={load}
        onBecomeReceipt={doc && !doc.id && doc.file_url ? becomeReceipt : undefined}
        onDelete={() => askDelete(doc?.ui_type === "vydana" ? "vydana" : "faktura", doc.id)}
        blocked={!doc?.id && blocked} onShowExisting={() => showDup(dups[0])}
        banner={!doc?.id && <DuplicateBanner dups={dups} onShow={showDup} />}
        onDiscard={() => { setReviewOpen(false); setDoc(null); }} nav={detailNav}
      docked={docked} onFileShown={setFileOpen} goRef={goRef} />
  );
  const recEl = (
    <ReceiptDetail open={recOpen} onOpenChange={(o) => { setRecOpen(o); if (!o) setRec(null); }} rec={rec} setRec={setRec} isNew={isNewRec}
        cats={rcats} projects={projects} busy={busy} blocked={blocked}
        hint={<KindSwitchedHint rec={rec} onOpenAsInvoice={openAsInvoice} />}
        banner={isNewRec && <DuplicateBanner dups={dups} onShow={showDup} />}
        onSave={saveRec} onSaveEdit={saveRecEdit} onDelete={() => removeRec(rec.id)}
        onDiscard={() => { setRecOpen(false); setRec(null); }} onShowExisting={() => showDup(dups[0])} nav={detailNav} docked={docked} onFileShown={setFileOpen}
      canTag={canTag} onManageTags={() => setTagsMgr(true)} />
  );
  const dialogs = (
    <>
      {/* OCR loading */}
      <Dialog open={ocrLoading}>
        <DialogContent className="max-w-xs [&>button]:hidden" data-testid="ocr-loading">
          <DialogHeader className="sr-only"><DialogTitle>Načítám doklad</DialogTitle><DialogDescription>OCR čte položky z dokladu</DialogDescription></DialogHeader>
          {OCR_SPIN}
        </DialogContent>
      </Dialog>

      {!docked && reviewEl}
      {!docked && recEl}

      <PaymentsDialog doc={payDoc?.raw} direction={payDoc?.kind === "vydana" ? "in" : "out"}
        onClose={() => setPayDoc(null)}
        onChanged={(updated) => {
          // 220: endpoint vrací aktualizovaný doklad — srovná se jen jeho řádek (dřív celé load()).
          setPayDoc((p) => ({ ...p, raw: updated }));
          if (payDoc?.kind === "vydana") setIssued((l) => mergeUpdatedRow(l, updated));
          else setDocs((l) => mergeUpdatedRow(l, updated));
          onChanged?.();   // Ekonomika zakázky přepočte KPI (inkaso)
        }} />

      <DeleteDocumentDialog target={delTarget} onClose={() => setDelTarget(null)} onDeleted={onDeleted} />
      {canTag && <TagsManagerDialog open={tagsMgr} onOpenChange={setTagsMgr} onChanged={() => onChanged?.()} />}
      {canBulk && <BulkDeleteDialog open={bulkOpen} rows={selRows} onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); clearSel(); load(); }} />}
    </>
  );
  if (dialogsOnly) return dialogs;

  return (
    <div className="space-y-5" data-testid="economics-documents">
      {/* KPI pás — za vybrané období, nefiltruje se chipy ani hledáním. V zakázce ne (má vlastní KPI). */}
      {/* 352: na telefonu KPI v jednom posuvném řádku (dřív 4 karty pod sebou = seznam až na druhé obrazovce) */}
      {!embedded && <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x scroll-px-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible [&>*]:min-w-[210px] [&>*]:snap-start sm:[&>*]:min-w-0" data-testid="doc-kpis">
        <StatCard icon={FileStack} label={perF ? "Doklady v období" : "Doklady celkem"} value={kpi.count + vydaneInPeriod}
          sub={`${plur(kpi.invoices, "faktura", "faktury", "faktur")} · ${plur(kpi.receipts, "účtenka", "účtenky", "účtenek")} · ${plur(vydaneInPeriod, "vydaná", "vydané", "vydaných")}`}
          testid="kpi-count" />
        <StatCard icon={Wallet} label={am === "gross" ? "Celkem s DPH" : "Celkem bez DPH"} value={formatCZK(kpi.total)} brand
          sub={am === "both" ? `s DPH ${formatCZK(kpi.totalGross)}` : kpi.overhead > 0 ? `z toho režie ${formatCZK(kpi.overhead)}` : "vše na zakázkách"} testid="kpi-total" />
        <StatCard icon={FileText} label="Nezaplaceno" value={formatCZK(kpi.unpaid)}
          sub={`${plur(kpi.unpaidCount, "doklad", "doklady", "dokladů")} · s DPH`} testid="kpi-unpaid" />
        <StatCard icon={FileText} label="Po splatnosti" value={formatCZK(kpi.overdue)}
          valueClass="text-destructive" testid="kpi-overdue"
          sub={kpi.overdueCount ? `${plur(kpi.overdueCount, "doklad", "doklady", "dokladů")} · nejstarší ${plur(kpi.oldest, "den", "dny", "dní")}` : "nic po splatnosti"} />
      </div>}

      {docPicker.inputs}
      {embedded ? (
        <div className="flex items-center gap-3">
          <h3 className="font-bold flex items-center gap-2 min-w-0"><FileText size={18} className="text-brand shrink-0" /> <span className="truncate">Doklady<span className="hidden sm:inline"> zakázky</span></span></h3>
          <ListActions {...headerActions} />
          {headerExtra}
        </div>
      ) : <SlotActions {...headerActions} />}

      {/* Duplicitní doklady (153) — dvojice k vyřešení, přímo nad lištou filtrů */}
      {!embedded && <DuplicatesPanel data={dupPairs} onOpen={(kind, id) => { if (!openById(kind, id)) toast.error("Doklad už v seznamu není."); }}
        onChanged={() => window.dispatchEvent(new Event("pp-doc-saved"))} />}

      <ListToolbar mobile={mobile} testid="doc-filterbar"
        tabs={[
          { value: "all", label: "Vše", count: counts.all },
          { value: "faktury", label: "Přijaté", count: counts.faktury },
          ...(issuedOk ? [{ value: "vydane", label: "Vydané", count: counts.vydane }] : []),
          { value: "uctenky", label: "Účtenky", count: counts.uctenky },
        ]}
        tab={typeF} onTab={setTypeF} filter={F} search={q} onSearch={setQ} searchPh="Dodavatel, číslo, FP 444, zakázka…"
        sort={{ icon: SlidersHorizontal, title: "Zobrazení", label: "Zobrazení", hotkey: "v", width: 620,   // 378: jen „Zobrazení“ (V), nastavení v bublině po najetí
          hint: `${sort.g !== "none" ? `${DOC_GROUPS[sort.g].label} · ` : ""}${DOC_ORDERS[sort.k].label} ${sort.dir === "asc" ? "↑" : "↓"} · ${AM_HINT[am]}`,
          active: !isDefView(sort, DOC_VIEW) || !!sort.a,   // 380: svítí jen mimo výchozí, Obnovit výchozí v bublině
          render: ({ mobile: m }) => (
            <GroupSortPanel mobile={m} groupFields={GROUP_FIELDS} group={sort.g === "none" ? null : { k: sort.g, dir: sort.gd || 0 }}
              onGroup={(g) => setSort({ ...sort, g: g || "none", gd: 0 })} onGroupDir={(gd) => setSort({ ...sort, gd })}
              orderFields={ORDER_FIELDS} order={{ k: sort.k, dir: sort.dir === "desc" ? 0 : 1 }}
              onOrder={(k) => setSort({ ...sort, k, dir: DOC_ORDERS[k].def })} onOrderDir={(i) => setSort({ ...sort, dir: i === 0 ? "desc" : "asc" })}
              onFold={(all) => setClosedGroups(all ? new Set((groups || []).map((g) => g.key)) : new Set())}
              defText={`${describeView(DOC_GROUPS, DOC_ORDERS, DOC_VIEW)} · ${AM_HINT[defAm]}`}
              onReset={isDefView(sort, DOC_VIEW) && !sort.a ? undefined : () => setSort({ ...DOC_VIEW })}
              extra={<AmountPick am={am} defAm={defAm} mobile={m} onPick={(a) => setSort({ ...sort, a: a === defAm ? null : a })} />} />
          ) }} />

      {/* Seznam (220): jen jedna varianta podle šířky (dřív tabulka i karty naráz, jedna skrytá CSS),
          postupně po PAGE řádcích. Desktop tabulka, mobil karty. */}
      {(() => {
        const list = (
          <DocList rows={shown} total={ordered.length} embedded={embedded} mobile={mobile} filtered={F.filterCount > 0}
            onOpen={onRowOpen} onPay={onRowPay} onDelete={onRowDelete} onMore={onMore} navSel={kb.sel} help={kb.help} setHelp={kb.setHelp}
            selectable={selectable} selected={selected} onToggle={toggleSel} allSel={allSel} someSel={selRows.length > 0} onToggleAll={toggleAll}
            groups={groups} groupBy={sort.g} closed={closedGroups} onToggleGroup={toggleGroup}
            tagColors={canTag ? tagColors : null} onTag={onRowTag} onManageTags={onManageTags} compact={docked && detailOpen} rowNav={rowNav} am={am} />
        );
        if (!docked || !detailOpen) return list;
        // 370: tabulka 2 : detail 3, stejná výška jako v Úkolech; ukázaná faktura tabulku schová
        // 374: min. výška okna — i u krátké tabulky jde stránka posunout tak, aby panel začínal nahoře
        return (
          <div ref={splitRef} className="flex items-start gap-4 scroll-mt-6 min-h-[calc(100vh-48px)]" data-testid="docs-split">
            {!fileOpen && <div className="flex-[2_1_0] min-w-0 space-y-5">{list}</div>}
            <DetailDock label="Detail dokladu" testid="doc-dock" className="flex-[3_1_0] min-w-[440px]">
              {reviewOpen && doc ? reviewEl : recEl}
            </DetailDock>
          </div>
        );
      })()}

      {/* 283: lišta označených — na telefonu nad spodní navigací, na PC dole v obsahu */}
      {canSelect && (selRows.length > 0 || (mobile && selMode)) && (
        <div className="fixed md:sticky z-40 left-3 right-3 md:left-auto md:right-auto bottom-[calc(var(--bottom-nav-h,5rem)+10px+env(safe-area-inset-bottom))] md:bottom-4 flex items-center gap-2 flex-wrap bg-card border border-border rounded-[18px] px-4 py-2.5 shadow-[0_18px_44px_rgba(0,0,0,0.16)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
          data-testid="docs-bulk-bar">
          <div className="min-w-0 mr-auto">
            <div className="font-bold text-[15px] tabular-nums" data-testid="docs-bulk-count">
              {selRows.length ? `Vybráno ${plur(selRows.length, "doklad", "doklady", "dokladů")}` : "Klepnutím vyberte doklady"}
            </div>
            {selRows.length > 0 && <div className="text-xs text-muted-foreground tabular-nums">{selSumText(selRows, amBase)}</div>}
          </div>
          <div className="flex w-full md:w-auto items-center justify-end gap-1">
            {!allSel && merged.length > 1 && (
              <Button variant="ghost" onClick={toggleAll} className="h-10 rounded-full px-3 font-semibold mr-auto md:mr-0" data-testid="docs-select-all">
                Vybrat vše ({merged.length.toLocaleString("cs-CZ")})
              </Button>
            )}
            <Button variant="ghost" onClick={clearSel} className="h-10 rounded-full px-3 font-semibold" data-testid="docs-select-clear">Zrušit</Button>
            {canTag && (
              <Popover open={bulkTagOpen} onOpenChange={setBulkTagOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" disabled={!selRows.length} className="h-10 rounded-full px-4 font-bold" data-testid="docs-bulk-tag"><Tags size={16} /> Přiřadit štítek</Button>
                </PopoverTrigger>
                <PopoverContent side="top" align="end" className="p-1.5 w-[260px] rounded-[14px]" onOpenAutoFocus={(e) => e.preventDefault()}>
                  {bulkTagOpen && <TagMenu onPick={bulkTag} onManage={() => { setBulkTagOpen(false); setTagsMgr(true); }} testid="bulk-tag-menu" />}
                </PopoverContent>
              </Popover>
            )}
            {canBulk && (
              <Button variant="destructive" disabled={!selRows.length} onClick={() => setBulkOpen(true)} data-testid="docs-bulk-delete"
                className="h-10 rounded-full px-4 font-bold">
                <Trash2 size={16} /> Smazat{selRows.length ? ` (${selRows.length.toLocaleString("cs-CZ")})` : ""}
              </Button>
            )}
          </div>
        </div>
      )}
      {/* místo pro lištu na telefonu, ať nezakryje poslední kartu */}
      {canSelect && mobile && (selRows.length > 0 || selMode) && <div className="h-24" aria-hidden="true" />}

      {!embedded && stockQ.count > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="stock-hint">
          <Package size={14} className="inline mb-0.5 text-amber-600" />{" "}
          <b className="text-foreground">{plur(stockQ.count, "položka čeká", "položky čekají", "položek čeká")} na naskladnění</b> · {formatCZK(stockQ.total)} —{" "}
          <Link to="/ekonomika/naskladneni" className="text-brand font-bold hover:underline">otevřít záložku Naskladnění</Link>
        </p>
      )}

      {/* 222: dvojice označené jako různé doklady — sbalené na konci stránky */}
      {!embedded && <DismissedDuplicates data={dupPairs} onChanged={() => { load(); window.dispatchEvent(new Event("pp-doc-saved")); }} />}

      {dialogs}
    </div>
  );
}

// Komponenty řádku na úrovni modulu (220): uvnitř renderu vznikal při každém
// překreslení nový typ komponenty a React všechny řádky odmontoval a namontoval znovu.
function StatusPill({ x }) {
  if (x.overdue) return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-destructive/[0.08] text-destructive whitespace-nowrap">{plur(x.overdueDays, "den", "dny", "dní")} po spl.</span>;
  if (x.status === "Zaplaceno") return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-success/[0.09] text-success whitespace-nowrap">Zaplaceno</span>;
  if (x.status === "Částečně zaplaceno") return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-blue-500/[0.09] text-blue-600 dark:text-blue-400 whitespace-nowrap">Částečně</span>;
  return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-amber-500/[0.09] text-amber-600 dark:text-amber-400 whitespace-nowrap">Nezaplaceno</span>;
}
const iconColor = (x) => x.overdue ? "text-destructive" : x.status === "Zaplaceno" ? "text-success"
  : x.status === "Částečně zaplaceno" ? "text-blue-600 dark:text-blue-400" : "text-amber-600 dark:text-amber-400";
// 224: přijatá faktura je výchozí druh (má vlastní ikonu) — štítek jen u výjimek, ať zbyde místo na dodavatele.
function TypeTag({ kind }) {
  if (kind === "faktura") return null;
  return (
    <span className={`text-[10px] font-bold uppercase rounded-md px-2 py-0.5 shrink-0 ${
      kind === "vydana" ? "bg-brand/15 text-brand" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
      {kind === "vydana" ? "Vydaná" : "Účtenka"}
    </span>
  );
}
// 224/229: interní číslo dokladu (FP 444, UC 46, FV 33) na začátku druhého řádku — nezkracuje dodavatele.
function DocNo({ x, mobile }) {
  return x.no ? (
    <span className="font-semibold tabular-nums text-foreground/70" title="Interní číslo dokladu" data-testid={mobile ? undefined : `doc-no-${x.id}`}>{x.series || "FP"} {x.no} · </span>
  ) : null;
}
// 224: akce řádku v jednom menu „⋯" (dřív peněženka + koš). Na mobilu zůstává tlačítko Platba vedle.
function RowMenu({ x, rest, mobile, onOpen, onPay, onDelete }) {
  const canPay = !mobile && (x.kind === "faktura" || x.kind === "vydana") && (rest > 0.5 || x.paid > 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Další akce" title="Platba, úprava, smazání" data-testid={`row-menu-${mobile ? "m-" : ""}${x.id}`}
          className={`${mobile ? "h-10 w-10" : "h-9 w-9"} inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors`}>
          <MoreHorizontal size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[190px]">
        {canPay && (
          <DropdownMenuItem onClick={() => onPay(x)} data-testid={`pay-btn-${x.id}`} className="py-2.5 font-semibold">
            <Wallet size={16} className="mr-2" /> {rest > 0.5 ? "Zadat platbu" : "Platby"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onOpen(x)} data-testid={`edit-${mobile ? "m-" : ""}${x.id}`} className="py-2.5 font-semibold">
          <PencilLine size={16} className="mr-2" /> Upravit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(x)} className="py-2.5 font-semibold text-destructive focus:text-destructive"
          data-testid={mobile ? `delete-m-${x.id}` : x.kind === "faktura" ? `delete-doc-${x.id}` : x.kind === "vydana" ? `delete-issued-${x.id}` : `delete-receipt-${x.id}`}>
          <Trash2 size={16} className="mr-2" /> Smazat
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
// Zálohy (171): konečná faktura bez odečtených záloh upozorní v řádku pod názvem
// (vyúčtovaná záloha je šedá a v podtitulku má „vyúčtováno fakturou č. …").
function AdvanceHint({ x }) {
  return x.advPending ? (
    <span className="font-semibold text-amber-600 dark:text-amber-400" data-testid={`adv-pending-${x.id}`}>Zálohy k vyúčtování · </span>
  ) : null;
}
function CreditPill({ x }) {
  return x.credit ? (
    <span className="text-[10px] font-bold uppercase rounded-md px-2 py-0.5 shrink-0 bg-destructive/15 text-destructive" data-testid={`credit-pill-${x.id}`}>Dobropis</span>
  ) : null;
}
// 373: tabulka jako v odsouhlaseném náhledu (Linear) — jedna karta, hlavička jednou nahoře, skupiny jako řádky
// uvnitř. Řádek: stav (při najetí zaškrtávátko), číslo FP, směr + dodavatel + č. dokladu na jednom řádku, štítek,
// zakázka jako pilulka, splatnost slovy, částka; ⋯ až při najetí. Klik kamkoli do řádku otevře detail.
// Celé třídy kvůli Tailwind JIT; v zakázce bez sloupce Zakázka, bez ekonomické role bez Štítku.
// Doklad a Zakázka se dělí o volné místo 2,2 : 1, ať na 1280 px nezůstane z dokladu jen kousek názvu.
const GRIDS = {
  fullTag: "grid-cols-[30px_56px_minmax(0,2.2fr)_132px_minmax(120px,1fr)_136px_140px_32px]",
  full: "grid-cols-[30px_56px_minmax(0,2.2fr)_minmax(120px,1fr)_136px_140px_32px]",
  embTag: "grid-cols-[30px_56px_minmax(0,1fr)_132px_136px_140px_32px]",
  emb: "grid-cols-[30px_56px_minmax(0,1fr)_136px_140px_32px]",
};
// 370: s otevřeným detailem vedle jen Stav · Číslo · Doklad · Částka (jako Úkoly: Název · Termín)
const GRID_COMPACT = "grid-cols-[30px_56px_minmax(0,1fr)_minmax(96px,auto)]";
const gridFor = (embedded, tags) => GRIDS[`${embedded ? "emb" : "full"}${tags ? "Tag" : ""}`];
const MIN_W = { fullTag: "min-w-[940px]", full: "min-w-[810px]", embTag: "min-w-[780px]", emb: "min-w-[640px]" };

// Ikona stavu jako v náhledu: kroužek s tečkou = čeká na úhradu, půlka = částečně, výseč = po splatnosti, fajfka = zaplaceno
const STAV_COLOR = { plan: "text-indigo-500 dark:text-indigo-400", part: "text-amber-500", late: "text-destructive", done: "text-success" };
const STAV_TITLE = { plan: "Čeká na úhradu", part: "Částečně zaplaceno", late: "Po splatnosti", done: "Zaplaceno" };
export function StavIcon({ s }) {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" className={`shrink-0 ${STAV_COLOR[s] || "text-muted-foreground"}`} aria-hidden="true">
      {s === "done" ? (<><circle cx="9" cy="9" r="8" fill="currentColor" />
        <path d="M5.6 9.2l2.3 2.3 4.5-4.6" fill="none" style={{ stroke: "hsl(var(--card))" }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></>)
        : (<><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
          {s === "plan" && <circle cx="9" cy="9" r="2.4" fill="currentColor" />}
          {s === "part" && <path d="M9 4.2A4.8 4.8 0 0 1 9 13.8z" fill="currentColor" />}
          {s === "late" && <path d="M9 4.2A4.8 4.8 0 0 1 13.2 11.4L9 9z" fill="currentColor" />}</>)}
    </svg>
  );
}
function ProjectPill({ x }) {
  const split = x.raw?.assignment_mode === "split";
  return (
    <span className={`inline-flex items-center gap-1.5 h-6 max-w-full px-2.5 rounded-full border border-border text-[12.5px] font-semibold whitespace-nowrap ${x.hasProject || split ? "" : "text-muted-foreground"}`}
      title={split ? "Rozděleno po položkách" : x.project} data-testid={`doc-project-${x.id}`}>
      {split ? <Layers size={12} className="shrink-0" />
        : <i className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: x.hasProject ? personColor(x.project_id) : "hsl(var(--muted-foreground) / .5)" }} />}
      <span className="truncate">{split ? "Rozděleno" : x.project}</span>
    </span>
  );
}
// Splatnost slovy jako v náhledu: „do 9. 11.“, „12 dní po splatnosti“, „zaplaceno 6. 9.“, u účtenky „hotově 28. 10.“
const PAY_WORD = { Hotovost: "hotově", Kartou: "kartou" };
const lastPaidDate = (x) => (x.raw?.payments || []).map((p) => p.date).filter(Boolean).sort().pop();
function dueInfo(x) {
  if (x.kind === "uctenka") return { t: `${PAY_WORD[x.raw?.payment_method] || "zaplaceno"} ${x.date ? formatDateShort(x.date) : ""}`.trim() };
  if (docStav(x) === "done") { const p = lastPaidDate(x); return { t: x.settled ? "vyúčtováno" : p ? `zaplaceno ${formatDateShort(p)}` : "zaplaceno" }; }
  if (x.overdue) return { t: `${plur(Math.max(x.overdueDays || 0, 1), "den", "dny", "dní")} po splatnosti`, late: true };
  return { t: x.due ? `do ${formatDateShort(x.due)}` : "—" };
}
const DOC_KEYS = [["↑ ↓", "vybrat"], ["mezerník", "otevřít a zavřít detail"], ["Z", "zakázka"], ["K", "kategorie"], ["L", "štítek"], ["F", "filtr"], ["V", "zobrazení"]];
function KeysHint({ upload, help, setHelp }) {
  return (
    <div className="hidden md:flex items-center flex-wrap gap-x-4 gap-y-1.5 px-1 text-xs text-muted-foreground" data-testid="docs-keys">
      {[...DOC_KEYS, ...(upload ? [["N", "nahrát"]] : [])].map(([k, t]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <kbd className="rounded-[5px] border border-border bg-secondary px-1.5 py-0.5 text-[11px] font-semibold font-sans text-foreground/80">{k}</kbd>{t}
        </span>
      ))}
      <ListHelp open={help} setOpen={setHelp} className="-my-1.5" />
    </div>
  );
}
// Lišta skupiny v tabulce na PC: šipka, (ikona stavu / barva štítku / barva zakázky), název, počet, vpravo součet
// 401: přijatá faktura a účtenka jsou náklad, vydaná tržba — součty ve Vše je nesmí sčítat dohromady
const isRev = (x) => x.kind === "vydana";
const signedAmt = (x, v) => (x.settled ? 0 : (x.credit ? -1 : 1) * v);
export function selSumText(rows, am) {
  const cost = rows.filter((x) => !isRev(x)), rev = rows.filter(isRev);
  const sum = (list) => formatCZK(list.reduce((t, x) => t + signedAmt(x, amountOf(x.amt, am)), 0));
  return [cost.length && `${sum(cost)} ${AM_HINT[am]}`, rev.length && `vydané ${sum(rev)}${cost.length ? "" : ` ${AM_HINT[am]}`}`].filter(Boolean).join(" · ");
}

function GroupRow({ g, groupBy, closed, onToggle, tagColors, am = "gross" }) {
  const tagColor = groupBy === "tag" && tagColors?.[g.key];
  const projColor = groupBy === "project" && g.key && !g.key.startsWith("~") ? personColor(g.rows[0]?.project_id) : null;
  // 389: s DPH jako dřív (nezaplacené skupiny = kolik zbývá zaplatit); bez DPH součet bez DPH a k úhradě zvlášť
  const pay = groupBy === "status" && g.key !== "done";
  const amount = am === "gross" ? (pay ? g.unpaid : g.sum) : g.sum;
  return (
    <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!closed} data-testid={`doc-group-${g.key || "none"}`}
      title={`${plur(g.rows.length, "doklad", "doklady", "dokladů")} · ${formatCZK(g.sum)}${g.unpaid > 0.5 ? ` · nezaplaceno ${formatCZK(g.unpaid)}` : ""}`}
      className="w-full flex items-center gap-2.5 min-h-[44px] px-4 border-t border-border bg-muted/40 hover:bg-muted/70 text-left transition-colors motion-reduce:transition-none">
      <ChevronDown size={16} className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${closed ? "-rotate-90" : ""}`} />
      {groupBy === "status" && <StavIcon s={g.key} />}
      {tagColor && <i className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: tagColor }} />}
      {projColor && <i className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: projColor }} />}
      <span className="font-bold text-[14px] truncate min-w-0">{g.label}</span>
      <span className="text-[12.5px] font-semibold text-muted-foreground tabular-nums">{g.rows.length}</span>
      <span className="ml-auto pl-3 text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">
        {(groupBy !== "status" || am !== "gross") && g.unpaid > 0.5 && <span className="text-amber-600 dark:text-amber-400">nezaplaceno {formatCZKCompact(g.unpaid)} · </span>}
        {formatCZKCompact(amount)}{am !== "gross" && <span className="text-[11.5px]"> bez DPH</span>}
        {am === "both" && <> · {formatCZKCompact(g.sumGross)}<span className="text-[11.5px]"> s DPH</span></>}
        {g.rev !== 0 && <> · vydané {formatCZKCompact(g.rev)}</>}
      </span>
    </button>
  );
}

// 365: lišta skupiny jako den v Docházce — šipka, název, vpravo počet, součet a nezaplaceno; klik sbalí
// 370: vedle otevřeného detailu (compact) jdou počet a součty pod název, ať se název nezkracuje
// 373: na telefonu zůstává; u Seskupit › Stav s ikonou stavu
function GroupBar({ g, closed, onToggle, tagColors, sortTag, stav = false, compact = false, am = "gross" }) {
  const color = sortTag && tagColors?.[g.key];
  return (
    <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!closed} data-testid={`doc-group-${g.key || "none"}`}
      className={`w-full flex items-center gap-3 min-h-[56px] px-4 md:px-5 py-2 bg-muted/50 hover:bg-muted text-left flex-wrap ${compact ? "" : "md:flex-nowrap"}`}>
      <ChevronDown size={18} className={`shrink-0 transition-transform motion-reduce:transition-none ${closed ? "-rotate-90" : ""}`} />
      <span className="font-heading font-extrabold text-[17px] md:text-[18px] tracking-tight flex items-center gap-2 min-w-0">
        {stav && <StavIcon s={g.key} />}{color && <i className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: color }} />}<span className="truncate">{g.label}</span>
      </span>
      <span className={`basis-full pl-[30px] text-[13px] font-semibold text-muted-foreground tabular-nums ${compact ? "" : "md:ml-auto md:basis-auto md:pl-0 md:whitespace-nowrap"}`}>
        <span className="whitespace-nowrap">{plur(g.rows.length, "doklad", "doklady", "dokladů")} · {formatCZK(g.sum)} {AM_HINT[am === "both" ? "net" : am]}</span>
        {am === "both" && <>{" "}<span className="whitespace-nowrap">· {formatCZK(g.sumGross)} s DPH</span></>}
        {g.rev !== 0 && <>{" "}<span className="whitespace-nowrap">· vydané {formatCZK(g.rev)}</span></>}
        {g.unpaid > 0.5 && <span className={`whitespace-nowrap text-amber-600 dark:text-amber-400 ${compact ? "inline" : "block md:inline"}`}><span className={compact ? "inline" : "hidden md:inline"}> · </span>nezaplaceno {formatCZK(g.unpaid)}</span>}
      </span>
    </button>
  );
}

// 283: zaškrtávátko výběru — velká klikací plocha, uvnitř 20px čtvereček (true / false / "mixed").
function SelBox({ checked, onClick, label, testid, big }) {
  const on = checked === true, mixed = checked === "mixed";
  return (
    <button type="button" role="checkbox" aria-checked={mixed ? "mixed" : on} aria-label={label} title={label}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }} data-testid={testid}
      className={`${big ? "h-11 w-11 -m-2.5" : "h-8 w-8 -m-1.5"} shrink-0 grid place-items-center rounded-full hover:bg-muted transition-colors motion-reduce:transition-none`}>
      {/* 373: čtvereček (rounded-md je v tématu skoro kruh a pletl by se s ikonou stavu) */}
      <span className={`h-[18px] w-[18px] rounded-[5px] border-2 grid place-items-center ${on || mixed ? "bg-brand border-brand text-white" : "border-muted-foreground/50 bg-card"}`}>
        {on ? <Check size={14} strokeWidth={3} /> : mixed ? <Minus size={14} strokeWidth={3} /> : null}
      </span>
    </button>
  );
}

const DocList = memo(function DocList({ rows, total, embedded, mobile, filtered, onOpen, onPay, onDelete, onMore,
  selectable, selected, onToggle, allSel, someSel, onToggleAll, navSel = -1, help = false, setHelp,
  groups = null, groupBy, closed, onToggleGroup, tagColors = null, onTag, onManageTags, compact = false, rowNav, am = "gross" }) {
  const isSel = (x) => !!selected?.has(`${x.kind}-${x.id}`);
  const empty = filtered ? "Žádný doklad neodpovídá filtru." : "Zatím žádné doklady. Nahrajte fakturu nebo účtenku.";
  const more = total > rows.length && (
    <div className="flex justify-center">
      <Button variant="outline" onClick={onMore} className="w-full md:w-auto h-11 rounded-full font-bold px-6" data-testid="docs-show-more">
        Zobrazit další · {rows.length.toLocaleString("cs-CZ")} z {total.toLocaleString("cs-CZ")}
      </Button>
    </div>
  );
  const tags = !!tagColors;
  const idx = new Map(rows.map((x, i) => [`${x.kind}-${x.id}`, i]));
  const inShown = (g) => g.rows.filter((x) => idx.has(`${x.kind}-${x.id}`));
  // 365: skupiny — vykreslí se lišta a pod ní jen řádky, které už jsou „zobrazené“ (postupné načítání)
  const visibleGroups = groups ? groups.filter((g) => closed?.has(g.key) || inShown(g).length > 0) : null;
  const cardProps = (x) => ({ x, embedded, onOpen, onPay, onDelete, selectable: selectable && !compact, sel: isSel(x), onToggle, tagColor: tags && !compact ? (tagColors[x.tag] || "#8a8f98") : null, onTag, onManageTags, compact, am });
  if (mobile) {
    const cards = (list) => list.map((x) => <DocCard key={`${x.kind}-${x.id}`} {...cardProps(x)} />);
    return (
      <div className="space-y-3" data-testid="doc-cards">
        {visibleGroups ? visibleGroups.map((g) => (
          <section key={g.key} className="space-y-3">
            <div className="rounded-[18px] overflow-hidden border border-border"><GroupBar g={g} closed={closed?.has(g.key)} onToggle={onToggleGroup} tagColors={tagColors} sortTag={groupBy === "tag"} stav={groupBy === "status"} am={am} /></div>
            {!closed?.has(g.key) && cards(inShown(g))}
          </section>
        )) : cards(rows)}
        {rows.length === 0 && !visibleGroups?.length && <p className="text-muted-foreground">{empty}</p>}
        {more}
      </div>
    );
  }
  const gk = `${embedded ? "emb" : "full"}${tags ? "Tag" : ""}`;
  const GRID = compact ? GRID_COMPACT : gridFor(embedded, tags);
  const anySel = !!selectable && !compact && (allSel || someSel);
  const head = (
    <div className={`group/head grid ${GRID} gap-3 items-center h-10 px-4 text-[12px] font-semibold text-muted-foreground`}>
      <span className="grid place-items-center">{selectable && !compact && (
        <span className={anySel ? "grid" : "grid opacity-0 group-hover/head:opacity-100 focus-within:opacity-100"}>
          <SelBox checked={allSel ? true : someSel ? "mixed" : false} onClick={onToggleAll} label="Vybrat všechny doklady podle filtru" testid="docs-select-header" />
        </span>)}</span>
      <span>Číslo</span><span>Doklad</span>
      {!compact && tags && <span>Štítek</span>}{!compact && !embedded && <span>Zakázka</span>}{!compact && <span>Splatnost</span>}
      <span className="text-right">{AM_HEAD[am]}</span>{!compact && <span />}
    </div>
  );
  const rowEls = (list) => list.map((x) => {
    const i = idx.get(`${x.kind}-${x.id}`);
    return <DocRow key={`${x.kind}-${x.id}`} {...cardProps(x)} i={i} navOn={navSel === i} grid={GRID} anySel={anySel} rowNav={rowNav} />;
  });
  return (
    <>
      {/* min-w + overflow-x-auto — bez toho se fr sloupce smrsknou na nulu */}
      <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="doc-table">
        <div className="overflow-x-auto">
          <div className={compact ? "" : MIN_W[gk]}>
            {head}
            {visibleGroups ? visibleGroups.map((g) => (
              <section key={g.key} data-testid="doc-group">
                <GroupRow g={g} groupBy={groupBy} closed={closed?.has(g.key)} onToggle={onToggleGroup} tagColors={tagColors} am={am} />
                {!closed?.has(g.key) && rowEls(inShown(g))}
              </section>
            )) : rowEls(rows)}
            {rows.length === 0 && !visibleGroups?.length && <p className="px-5 py-6 text-muted-foreground border-t border-border">{empty}</p>}
          </div>
        </div>
      </div>
      {more}
      {setHelp && <KeysHint upload={!embedded} help={help} setHelp={setHelp} />}
    </>
  );
});

// 389: pod částkou — u Obojí s DPH, u Bez DPH kolik zbývá zaplatit (vždy s DPH); doklad bez rozpisu DPH upozorní,
// plátci se do nákladu počítá celá částka, dokud se DPH nedoplní
const amountTitle = (x, rest) => `bez DPH ${formatCZK(x.amt.net)} · DPH ${x.amt.unknown ? "neuvedeno" : formatCZK(x.amt.vat)} · s DPH ${formatCZK(x.amt.gross)}`
  + (x.paid > 0 && rest > 0.5 ? ` · uhrazeno ${formatCZK(x.paid)}` : "");
function AmountNote({ x, am, rest, inline = false }) {
  const cls = `${inline ? "" : "block "}text-xs tabular-nums whitespace-nowrap`;
  const due = x.kind === "vydana" ? "neuhrazeno" : "k úhradě";   // vydanou platí zákazník
  const pay = (am === "net" || (inline && am === "both")) && !x.credit && !x.settled && rest > 0.5;   // telefon: k úhradě i u Obojí
  const warn = am !== "gross" && x.amt.unknown && x.kind !== "vydana" && x.amt.gross > 0;
  const out = (<>
    {am === "both" && <span className={`${cls} text-muted-foreground`}>s DPH {x.credit ? "−" : ""}{formatCZKCompact(x.amt.gross)}</span>}
    {pay && <span className={`${cls} font-semibold text-amber-600 dark:text-amber-400`}>{due} {formatCZKCompact(rest)}</span>}
    {warn && <span className={`${cls} font-semibold text-amber-600 dark:text-amber-400`} title="Doklad neuvádí DPH — počítá se celá částka. Doplňte DPH v detailu.">DPH neuvedeno</span>}
  </>);
  return inline ? <div className="flex flex-wrap gap-x-2 mt-0.5">{out}</div> : out;
}
function DocRow({ x, i, navOn, grid, embedded, onOpen, onPay, onDelete, selectable, sel, onToggle, tagColor, onTag, onManageTags, compact, anySel, rowNav, am = "gross" }) {
  const rest = Math.max((x.payable ?? x.total ?? 0) - (x.paid || 0), 0);
  const st = docStav(x), due = dueInfo(x);
  // klik kamkoli do řádku otevře detail — kromě zaškrtávátka, štítku a ⋯ (a nabídek z nich, které jsou v portálu)
  const open = (e) => { if (!e.currentTarget.contains(e.target) || e.target.closest("[data-row-stop]")) return; onOpen(x); };
  const sub = x.kind === "uctenka" ? [x.raw?.receipt_number && `účtenka ${x.raw.receipt_number}`, x.category].filter(Boolean).join(" · ") : x.sub;
  const [dirLabel, Dir] = x.kind === "uctenka" ? ["Účtenka", ReceiptText] : x.kind === "vydana" ? ["Vydaná faktura", ArrowUpRight] : ["Přijatá faktura", ArrowDownLeft];
  const showBox = anySel || sel;
  return (
    <div role="row" {...rowNav?.(i)} onClick={open} data-testid={x.kind === "faktura" ? `doc-${x.id}` : `receipt-${x.id}`} data-nav={`docs:${i}`} aria-selected={navOn || undefined} tabIndex={-1}
      className={`group grid ${grid} gap-3 items-center min-h-[44px] px-4 py-1 border-t border-border/60 cursor-pointer outline-none transition-colors motion-reduce:transition-none ${navOn ? "bg-brand/[0.06] shadow-[inset_2px_0_0_hsl(var(--accent-orange))]" : sel ? "bg-brand/[0.05]" : "hover:bg-muted/40"} ${x.settled ? "opacity-60" : ""}`}>
      <span className="grid place-items-center" title={STAV_TITLE[st]} data-testid={`doc-stav-${x.id}`} data-stav={st}>
        {selectable ? (<>
          <span className={showBox ? "hidden" : "grid group-hover:hidden"}><StavIcon s={st} /></span>
          <span data-row-stop className={showBox ? "grid" : "hidden group-hover:grid"}><SelBox checked={sel} onClick={() => onToggle(x)} label={`Vybrat ${x.supplier || "doklad"}`} testid={`doc-select-${x.id}`} /></span>
        </>) : <StavIcon s={st} />}
      </span>
      <span className="text-[12.5px] font-semibold tabular-nums text-muted-foreground whitespace-nowrap" title="Interní číslo dokladu" data-testid={`doc-no-${x.id}`}>{x.no ? `${x.series || "FP"} ${x.no}` : "—"}</span>
      <button type="button" data-row-stop onClick={() => onOpen(x)} title={[x.supplier || "Bez názvu", sub].filter(Boolean).join(" · ")}
        className="flex items-center gap-2 min-w-0 text-left whitespace-nowrap rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className="shrink-0 grid text-muted-foreground/70" title={dirLabel} aria-label={dirLabel}><Dir size={14} /></span>
        {/* dodavatel má přednost — číslo dokladu za ním se zkrátí (nebo zmizí) dřív */}
        <span className="font-semibold text-[14.5px] truncate shrink-0 max-w-full">{x.supplier || "Bez názvu"}</span>
        <CreditPill x={x} />
        {sub && <span className="text-[13px] text-muted-foreground truncate min-w-0"><AdvanceHint x={x} />{sub}</span>}
      </button>
      {tagColor && <span data-row-stop className="min-w-0 flex"><TagCell name={x.tag} color={tagColor} onPick={(t) => onTag(x, t)} onManage={onManageTags} testid={`doc-tag-${x.id}`} /></span>}
      {!embedded && !compact && <span className="min-w-0 flex"><ProjectPill x={x} /></span>}
      {!compact && <span className={`text-[13px] whitespace-nowrap truncate ${due.late ? "text-destructive font-semibold" : "text-muted-foreground"}`}
        title={`Vystaveno ${formatDate(x.date)}${x.due ? ` · splatnost ${formatDate(x.due)}` : ""}`}>{due.t}</span>}
      <span className="text-right min-w-0" title={amountTitle(x, rest)} data-testid={`doc-amount-${x.id}`}>
        <span className={`block font-bold tabular-nums whitespace-nowrap ${x.credit ? "text-destructive" : ""}`}>{x.credit ? "−" : ""}{formatCZKCompact(am === "gross" ? x.total : x.amt.net)}</span>
        <AmountNote x={x} am={am} rest={rest} />
        {x.fx && <span className="block text-xs text-muted-foreground tabular-nums whitespace-nowrap" title={x.fx.rate} data-testid={`doc-fx-${x.id}`}>{x.fx.text}</span>}
      </span>
      {!compact && <span data-row-stop className="flex justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
        <RowMenu x={x} rest={rest} onOpen={onOpen} onPay={onPay} onDelete={onDelete} /></span>}
    </div>
  );
}

function DocCard({ x, embedded, onOpen, onPay, onDelete, selectable, sel, onToggle, tagColor, onTag, onManageTags, am = "gross" }) {
  const Icon = x.kind === "faktura" ? FileText : ReceiptText;
  const rest = Math.max((x.payable ?? x.total ?? 0) - (x.paid || 0), 0);
  const dueAmt = x.payable ?? x.total;
  const pct = dueAmt > 0 ? Math.min(Math.round((x.paid / dueAmt) * 100), 100) : 0;
  return (
    <div data-testid={`doc-card-${x.id}`}
      className={`rounded-[18px] border p-4 bg-card ${sel ? "border-brand bg-brand/[0.05]" : "border-border"} ${!sel && x.overdue ? "bg-destructive/[0.03]" : ""} ${x.settled && !selectable ? "opacity-60" : ""}`}>
      <button onClick={() => (selectable ? onToggle(x) : onOpen(x))} className="flex items-start gap-2.5 text-left w-full min-w-0"
        role={selectable ? "checkbox" : undefined} aria-checked={selectable ? sel : undefined} data-testid={selectable ? `doc-select-m-${x.id}` : undefined}>
        {selectable
          ? <span className={`h-5 w-5 mt-0.5 shrink-0 rounded-md border-2 grid place-items-center ${sel ? "bg-brand border-brand text-white" : "border-muted-foreground/50"}`}>{sel && <Check size={14} strokeWidth={3} />}</span>
          : <Icon size={18} className={`shrink-0 mt-0.5 ${iconColor(x)}`} />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className="font-heading font-bold truncate">{x.supplier || "Bez názvu"}</span>
            <TypeTag kind={x.kind} />
            <CreditPill x={x} />
          </span>
          {/* 224: zakázka na vlastním řádku — na konci řádku s číslem byla vždy uříznutá */}
          {!embedded && <span className={`block text-[13px] truncate ${x.hasProject ? "font-semibold text-foreground/80" : "text-muted-foreground"}`} data-testid={`doc-card-project-${x.id}`}>{x.project}</span>}
          <span className="block text-[13px] text-muted-foreground truncate"><DocNo x={x} mobile /><AdvanceHint x={x} />{x.sub}</span>
        </span>
      </button>
      {x.paid > 0 && rest > 0.5 && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-success" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-muted-foreground mt-1 tabular-nums">uhrazeno {formatCZK(x.paid)} z {formatCZK(dueAmt)}</div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 border-t border-border pt-3 mt-3">
        <div className="min-w-0">
          <div className={`font-heading font-extrabold text-[19px] tabular-nums ${x.credit ? "text-destructive" : ""}`} data-testid={`doc-card-amount-${x.id}`}>{x.credit ? "−" : ""}{formatCZK(am === "gross" ? (rest > 0.5 ? rest : x.total) : x.amt.net)}
            {x.fx && <span className="ml-2 text-xs font-sans font-semibold text-muted-foreground" title={x.fx.rate}>{x.fx.text}</span>}</div>
          {am !== "gross" && <AmountNote x={x} am={am} rest={rest} inline />}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap"><StatusPill x={x} />{x.due && <span className="text-xs text-muted-foreground" title={formatDate(x.due)}>splatnost {formatDateShort(x.due)}</span>}
            {tagColor && !selectable && <TagCell name={x.tag} color={tagColor} onPick={(t) => onTag(x, t)} onManage={onManageTags} testid={`doc-tag-m-${x.id}`} />}</div>
        </div>
        {!selectable && <div className="flex items-center gap-1 shrink-0">
          {(x.kind === "faktura" || x.kind === "vydana") && (rest > 0.5 || x.paid > 0) && (
            <Button variant="outline" size="sm" onClick={() => onPay(x)} data-testid={`pay-btn-m-${x.id}`} className="h-9 rounded-full font-bold"><Wallet size={14} /> {rest > 0.5 ? (x.kind === "vydana" ? "Inkasovat" : x.paid > 0 ? "Doplatit" : "Platba") : "Platby"}</Button>
          )}
          <RowMenu x={x} rest={rest} mobile onOpen={onOpen} onPay={onPay} onDelete={onDelete} />
        </div>}
      </div>
    </div>
  );
}

